import { MouseEvent } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMessageDots, faMessageCheck } from "@fortawesome/pro-duotone-svg-icons"
import { IconButton, Tooltip } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ChatIconVariant, ChatMessage, getChatIconVariant } from "./orderChatTypes"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { TranslationKey } from "../../../modules/localization/translations"

const chatIconConfig: Record<ChatIconVariant, { icon: typeof faMessageDots; tooltipKey: TranslationKey }> = {
    [ChatIconVariant.NEEDS_RESPONSE]: { icon: faMessageDots, tooltipKey: "order-chat-tooltip-needs-response" },
    [ChatIconVariant.RESPONDED]: { icon: faMessageCheck, tooltipKey: "order-chat-tooltip-responded" },
}

const useStyles = makeStyles<{ variant: ChatIconVariant | null }>()((theme, { variant }) => ({
    icon: {
        fontSize: "1.125rem",
        color:
            variant === ChatIconVariant.NEEDS_RESPONSE
                ? theme.palette.tailwind.blue[400]
                : variant === ChatIconVariant.RESPONDED
                  ? theme.palette.tailwind.slate[400]
                  : theme.palette.text.primary,
    },
    button: {
        padding: "0.125rem",
    },
}))

type OrderChatIconProps = Readonly<{
    messages?: ChatMessage[]
    onClick: () => void
    className?: string
}>

export function OrderChatIcon({ messages, onClick, className }: OrderChatIconProps) {
    const variant = getChatIconVariant(messages)
    const { classes, cx } = useStyles({ variant })
    const { l10n } = useAppLocalization()

    if (!variant) return null

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onClick()
    }

    const { icon, tooltipKey } = chatIconConfig[variant]
    const lastMessage = messages?.[0]
    const tooltipTitle = lastMessage?.body ?? l10n.getString(tooltipKey)

    return (
        <Tooltip title={tooltipTitle} arrow>
            <IconButton size="small" onClick={handleClick} className={cx(classes.button, className)}>
                <FontAwesomeIcon icon={icon} className={classes.icon} />
            </IconButton>
        </Tooltip>
    )
}
